/**
 * SubmissionWizard - Guided Portal Submission Component
 * 
 * Phase 1A: Clipboard-First Guided Submission
 * 
 * This wizard guides users through submitting appeals/transfers to councils
 * via their online portals with clipboard assistance and proof tracking.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Copy, 
  ExternalLink, 
  CheckCircle, 
  AlertCircle,
  Info,
  ArrowRight,
  ArrowLeft,
  Search,
  Check
} from 'lucide-react';
import { 
  detectAuthority, 
  getRecommendedSubmissionMethod,
  getAllAuthorities,
  createSubmission,
  uploadProofScreenshot,
  SubmissionType,
  ProofType
} from '../services/rpaService';
import { IssuingAuthority, SubmissionMethod } from '../data/issuingAuthorities';
import { Ticket } from '../types';
import { useToast } from '../context/ToastContext';
import {
  SelectableOptionButton,
  submissionFieldLabelClass,
  submissionHelperTextClass,
  submissionInputClass,
} from './submissionFlowPrimitives';

interface SubmissionWizardProps {
  ticket: Ticket;
  submissionType: SubmissionType;
  appealText?: string;
  onComplete: () => void;
  onCancel: () => void;
}

export default function SubmissionWizard({
  ticket,
  submissionType,
  appealText,
  onComplete,
  onCancel
}: SubmissionWizardProps) {
  const { addToast } = useToast();
  const [step, setStep] = useState(1);
  const [authority, setAuthority] = useState<IssuingAuthority | undefined>();
  const [submissionMethod, setSubmissionMethod] = useState<SubmissionMethod | undefined>();
  const [proofType, setProofType] = useState<ProofType | undefined>();
  const [proofFile, setProofFile] = useState<File | undefined>();
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedFields, setCopiedFields] = useState<Set<string>>(new Set());
  const [selectedAuthorityId, setSelectedAuthorityId] = useState('');
  const [authorityQuery, setAuthorityQuery] = useState('');
  const allAuthorities = useMemo(
    () => [...getAllAuthorities()].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );
  const filteredAuthorities = useMemo(() => {
    const normalizedQuery = authorityQuery.trim().toLowerCase();
    if (!normalizedQuery) return allAuthorities;

    return allAuthorities.filter((candidate) => {
      const nameMatch = candidate.name.toLowerCase().includes(normalizedQuery);
      const keyMatch = candidate.matchKeys.some((key) => key.toLowerCase().includes(normalizedQuery));
      return nameMatch || keyMatch;
    });
  }, [allAuthorities, authorityQuery]);
  const groupedAuthorities = useMemo(() => {
    const statutory = filteredAuthorities.filter((candidate) => candidate.type !== 'private_operator');
    const privateOperators = filteredAuthorities.filter((candidate) => candidate.type === 'private_operator');
    return { statutory, privateOperators };
  }, [filteredAuthorities]);

  // Auto-detect authority on mount
  useEffect(() => {
    const detect = async () => {
      const detected = await detectAuthority({
        issuing_authority: ticket.issuing_authority,
        location: ticket.location,
        pcn_number: ticket.pcn_number
      });
      
      if (detected) {
        setAuthority(detected);
        setSelectedAuthorityId(detected.id);
        const method = getRecommendedSubmissionMethod(detected);
        setSubmissionMethod(method);
      } else {
        setAuthority(undefined);
        setSelectedAuthorityId('');
        setSubmissionMethod(undefined);
      }
    };
    detect();
  }, [ticket]);

  const handleAuthoritySelection = (authorityId: string) => {
    setSelectedAuthorityId(authorityId);

    if (!authorityId) {
      setAuthority(undefined);
      setSubmissionMethod(undefined);
      return;
    }

    const selected = allAuthorities.find((candidate) => candidate.id === authorityId);
    setAuthority(selected);
    setSubmissionMethod(selected ? getRecommendedSubmissionMethod(selected) : undefined);
  };

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFields(prev => new Set(prev).add(fieldName));
      addToast('Copied to clipboard!', 'success');
      setTimeout(() => {
        setCopiedFields(prev => {
          const next = new Set(prev);
          next.delete(fieldName);
          return next;
        });
      }, 2000);
    } catch (error) {
      addToast('Failed to copy to clipboard', 'error');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProofFile(file);
      setProofType('screenshot');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let proofUrl: string | undefined;
      
      // Upload proof screenshot if provided
      if (proofFile) {
        proofUrl = await uploadProofScreenshot(ticket.id, proofFile);
      }

      // Create submission record
      await createSubmission({
        ticket_id: ticket.id,
        submission_type: submissionType,
        method: submissionMethod?.type === 'portal' ? 'portal' : 'other',
        proof_type: proofType,
        proof_url: proofUrl,
        reference_number: referenceNumber || undefined,
        notes: notes || undefined
      });

      addToast('Submission recorded successfully!', 'success');
      onComplete();
    } catch (error) {
      console.error('Submission error:', error);
      addToast('Failed to record submission', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((s) => (
        <React.Fragment key={s}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
            s === step ? 'bg-blue-600 text-white' :
            s < step ? 'bg-green-600 text-white' :
            'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
          }`}>
            {s < step ? <CheckCircle className="w-5 h-5" /> : s}
          </div>
          {s < 4 && (
            <div className={`w-16 h-1 ${s < step ? 'bg-green-600' : 'bg-slate-200 dark:bg-slate-700'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Step 1: Confirm Authority</h3>

      {authority ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 mr-3" />
            <div>
              <p className="font-medium text-green-900 dark:text-green-100">Authority Detected</p>
              <p className="text-green-800 dark:text-green-200">{authority.name}</p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                Type: {authority.type === 'council' ? 'Council' : 'Private Operator'}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3" />
            <div>
              <p className="font-medium text-yellow-900 dark:text-yellow-100">Authority Not Detected</p>
              <p className="text-yellow-800 dark:text-yellow-200">Please select manually from the list below.</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className={`${submissionFieldLabelClass} mb-2`}>
          Issuing Authority
        </label>
        <div className="relative mb-2">
          <Search className="w-4 h-4 text-slate-500 dark:text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={authorityQuery}
            onChange={(e) => setAuthorityQuery(e.target.value)}
            placeholder="Search authority name"
            className={`${submissionInputClass} pl-9 pr-3`}
          />
        </div>
        <div className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 max-h-64 overflow-y-auto p-2 space-y-3">
          <SelectableOptionButton
            onClick={() => handleAuthoritySelection('')}
            selected={!selectedAuthorityId}
          >
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">No Manual Selection</span>
              {!selectedAuthorityId && <Check className="w-4 h-4" />}
            </div>
          </SelectableOptionButton>

          <div>
            <p className="px-1 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
              Councils and TfL
            </p>
            <div className="space-y-1">
              {groupedAuthorities.statutory.map((item) => {
                const isSelected = selectedAuthorityId === item.id;
                return (
                  <SelectableOptionButton
                    key={item.id}
                    onClick={() => handleAuthoritySelection(item.id)}
                    selected={isSelected}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">{item.name}</span>
                      {isSelected && <Check className="w-4 h-4" />}
                    </div>
                  </SelectableOptionButton>
                );
              })}
              {groupedAuthorities.statutory.length === 0 && (
                <p className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">No council/TfL matches.</p>
              )}
            </div>
          </div>

          <div>
            <p className="px-1 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
              Private Operators
            </p>
            <div className="space-y-1">
              {groupedAuthorities.privateOperators.map((item) => {
                const isSelected = selectedAuthorityId === item.id;
                return (
                  <SelectableOptionButton
                    key={item.id}
                    onClick={() => handleAuthoritySelection(item.id)}
                    selected={isSelected}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium">{item.name}</span>
                      {isSelected && <Check className="w-4 h-4" />}
                    </div>
                  </SelectableOptionButton>
                );
              })}
              {groupedAuthorities.privateOperators.length === 0 && (
                <p className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300">No private operator matches.</p>
              )}
            </div>
          </div>
        </div>
        <p className={`${submissionHelperTextClass} mt-2`}>
          Use manual selection if auto-detection is missing or incorrect.
        </p>
      </div>

      {submissionMethod && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-100">Recommended Method</p>
              <p className="text-blue-800 dark:text-blue-200">
                {submissionMethod.type === 'portal' ? 'Online Portal' :
                 submissionMethod.type === 'email' ? 'Email' :
                 submissionMethod.type === 'post' ? 'Post' : 'Other'}
              </p>
              {submissionMethod.notes && (
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">{submissionMethod.notes}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Step 2: Copy Information</h3>

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-sm text-blue-900 dark:text-blue-100">
            Click the copy buttons below to copy each field to your clipboard.
            You'll paste these into the council's portal in the next step.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {[
          { label: 'PCN Number', value: ticket.pcn_number, field: 'pcn' },
          { label: 'Vehicle Registration', value: ticket.vrn, field: 'vrn' },
          { label: 'Date of Issue', value: new Date(ticket.issue_date).toLocaleDateString(), field: 'date' },
          { label: 'Location', value: ticket.location, field: 'location' },
        ].map(({ label, value, field }) => (
          <div key={field} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded-lg">
            <div>
              <p className="text-sm text-slate-700 dark:text-slate-300">{label}</p>
              <p className="font-medium text-slate-900 dark:text-slate-100">{value}</p>
            </div>
            <button
              onClick={() => handleCopyToClipboard(value, field)}
              className={`px-3 py-2 rounded-lg transition-colors ${
                copiedFields.has(field)
                  ? 'bg-green-600 text-white'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {copiedFields.has(field) ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
        ))}

        {appealText && (
          <div className="p-3 bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-slate-700 dark:text-slate-300">Appeal Text</p>
              <button
                onClick={() => handleCopyToClipboard(appealText, 'appeal')}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  copiedFields.has('appeal')
                    ? 'bg-green-600 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {copiedFields.has('appeal') ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto bg-white dark:bg-slate-900 p-3 rounded border border-slate-300 dark:border-slate-600">
              <p className="text-sm text-slate-900 dark:text-slate-100 whitespace-pre-wrap">{appealText}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Step 3: Submit via Portal</h3>

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="font-medium text-yellow-900 dark:text-yellow-100 mb-1">Important</p>
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              PCNGuard prepares and assists. You submit and remain responsible for meeting deadlines.
            </p>
          </div>
        </div>
      </div>

      {submissionMethod?.url && (
        <div className="bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg p-4">
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">Click below to open the submission portal:</p>
          <a
            href={submissionMethod.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Open {authority?.name} Portal
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
          <p className="text-xs text-slate-600 dark:text-slate-300 mt-3">
            Opens in a new tab. Paste the information you copied in Step 2.
          </p>
        </div>
      )}

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-2">After submitting:</p>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 ml-4 list-disc">
          <li>Take a screenshot of the confirmation page</li>
          <li>Note down any reference number provided</li>
          <li>Save any confirmation email</li>
        </ul>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Step 4: Upload Proof</h3>

      <div className="space-y-4">
        <div>
          <label className={`${submissionFieldLabelClass} mb-2`}>
            Screenshot of Confirmation
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="block w-full text-sm text-slate-700 dark:text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900/30 file:text-blue-700 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50"
          />
          {proofFile && (
            <p className="text-sm text-green-700 dark:text-green-300 mt-2 flex items-center">
              <CheckCircle className="w-4 h-4 mr-1" />
              {proofFile.name}
            </p>
          )}
        </div>

        <div>
          <label className={`${submissionFieldLabelClass} mb-2`}>
            Reference Number (if provided)
          </label>
          <input
            type="text"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            placeholder="e.g., REF123456"
            className={submissionInputClass}
          />
        </div>

        <div>
          <label className={`${submissionFieldLabelClass} mb-2`}>
            Additional Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Any additional information about the submission..."
            className={submissionInputClass}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-950 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-slate-200 dark:border-slate-800">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Submit {submissionType === 'appeal' ? 'Appeal' : 'Transfer'}</h2>
          <p className="text-slate-700 dark:text-slate-300 mb-6">
            We'll guide you through submitting to {authority?.name || 'the council'}
          </p>

          {renderStepIndicator()}

          <div className="min-h-[400px]">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </div>

          <div className="flex justify-between mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
            <button
              onClick={step === 1 ? onCancel : () => setStep(step - 1)}
              className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {step === 1 ? 'Cancel' : 'Back'}
            </button>
            <button
              onClick={step === 4 ? handleSubmit : () => setStep(step + 1)}
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {step === 4 ? (isSubmitting ? 'Submitting...' : 'Complete') : 'Next'}
              {step < 4 && <ArrowRight className="w-5 h-5 ml-2" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

